import { makeAutoObservable, runInAction } from 'mobx';
import { appConfigAdminApi } from '@/services/appConfigAdminApi';
import type {
  AppConfigVersionMeta,
  AppConfigVersionViewItem,
  ChangeDesktopUpdateProductRequest,
  CreateGatewayPrincipalRequest,
  DesktopUpdateApproval,
  DesktopUpdatesDocumentView,
  GatewayPrincipalClientConfigSnippet,
  GatewayPrincipalClientConfigsView,
  GatewayPrincipalItem,
  GatewayPrincipalQuota,
  GatewayPrincipalsDocumentView,
  GatewayPrincipalTemplateDefaults,
  GatewayPrincipalTemplateView,
  GatewayPrincipalUsageDailyPoint,
  GatewayPrincipalUsageModelSummary,
  GatewayPrincipalUsageSummaryView,
  GatewayPrincipalViewItem,
  GetDesktopUpdatesDocumentReply,
  GetGatewayPrincipalClientConfigsReply,
  GetGatewayPrincipalsDocumentReply,
  GetGatewayPrincipalUsageSummaryReply,
  UpdateGatewayPrincipalRequest,
} from '@/types';

interface ApiErrorLike {
  message?: string;
  response?: {
    status?: number;
    data?: {
      error?: string;
      errmsg?: string;
      message?: string;
    };
  };
}

function isConflictError(error: unknown) {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as ApiErrorLike).response?.status === 409
  );
}

interface GatewayDocumentRefreshExpectation {
  expectedUID?: string;
  removedUID?: string;
  minVersionNumber?: number;
}

const GATEWAY_DOCUMENT_REFRESH_DELAYS_MS = [800, 1200, 2000, 3000, 5000, 8000];

function delay(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function getErrorMessage(error: unknown, fallback: string) {
  if (typeof error === 'object' && error !== null) {
    const apiError = error as ApiErrorLike;
    return (
      apiError.response?.data?.errmsg ||
      apiError.response?.data?.message ||
      apiError.response?.data?.error ||
      apiError.message ||
      fallback
    );
  }
  return fallback;
}

function emptyQuota(quota?: GatewayPrincipalQuota): GatewayPrincipalQuota {
  return {
    daily_usd: quota?.daily_usd || '',
    monthly_usd: quota?.monthly_usd || '',
    daily_tokens: quota?.daily_tokens || 0,
  };
}

function hasGatewayPrincipal(
  doc: GatewayPrincipalsDocumentView | null,
  uid?: string,
) {
  if (!doc || !uid) {
    return false;
  }
  return doc.principals.some((item) => item.uid === uid);
}

function gatewayDocumentMatchesExpectation(
  doc: GatewayPrincipalsDocumentView | null,
  expectation: GatewayDocumentRefreshExpectation,
) {
  if (!doc) {
    return false;
  }
  if (
    expectation.minVersionNumber !== undefined &&
    (doc.currentVersion?.versionNumber || 0) < expectation.minVersionNumber
  ) {
    return false;
  }
  if (
    expectation.expectedUID &&
    !hasGatewayPrincipal(doc, expectation.expectedUID)
  ) {
    return false;
  }
  if (
    expectation.removedUID &&
    hasGatewayPrincipal(doc, expectation.removedUID)
  ) {
    return false;
  }
  return true;
}

function toVersionViewItem(
  item: AppConfigVersionMeta,
  index: number,
): AppConfigVersionViewItem {
  return {
    id: `${item.configuration_name || 'app-config'}-${item.version_number}-${item.deployment_number}-${index}`,
    configurationName: item.configuration_name,
    versionNumber: item.version_number,
    versionLabel: item.version_label,
    contentType: item.content_type,
    deploymentNumber: item.deployment_number,
    deploymentState: item.deployment_state,
    startedAt: item.started_at,
    completedAt: item.completed_at,
  };
}

function isActiveDesktopApproval(approval: DesktopUpdateApproval) {
  return approval.status === 'pending' || approval.status === 'approved';
}

function compareDecimalIdsDescending(left: string, right: string) {
  const normalizedLeft = left.replace(/^0+(?=\d)/, '');
  const normalizedRight = right.replace(/^0+(?=\d)/, '');
  if (normalizedLeft.length !== normalizedRight.length) {
    return normalizedRight.length - normalizedLeft.length;
  }
  return normalizedLeft === normalizedRight
    ? 0
    : normalizedLeft < normalizedRight
      ? 1
      : -1;
}

function toPrincipalViewItem(
  item: GatewayPrincipalItem,
): GatewayPrincipalViewItem {
  return {
    id: item.uid,
    label: item.label,
    uid: item.uid,
    status: item.status,
    authMode: item.auth_mode,
    authTokenMasked: item.auth_token_masked,
    hasAuthToken: item.has_auth_token,
    allowedProtocols: item.allowed_protocols || [],
    allowedModels: item.allowed_models || [],
    quota: emptyQuota(item.quota),
    metadata: item.metadata || {},
  };
}

function toTemplateDefaults(
  value?: GatewayPrincipalTemplateDefaults,
): GatewayPrincipalTemplateView | null {
  if (!value) {
    return null;
  }
  return {
    status: value.status,
    authMode: value.auth_mode,
    allowedProtocols: value.allowed_protocols || [],
    allowedModels: value.allowed_models || [],
    quota: emptyQuota(value.quota),
    metadata: value.metadata || {},
  };
}

function toGatewayPrincipalClientConfigSnippetView(
  item: GatewayPrincipalClientConfigSnippet,
  index: number,
) {
  return {
    id: `${item.file_path}-${index}`,
    title: item.title,
    filePath: item.file_path,
    language: item.language,
    content: item.content,
  };
}

function toGatewayPrincipalClientConfigsView(
  response: GetGatewayPrincipalClientConfigsReply,
): GatewayPrincipalClientConfigsView {
  return {
    uid: response.uid,
    label: response.label,
    availableModels: response.available_models || [],
    selectedModel: response.selected_model || '',
    codexSnippets: response.codex_snippets.map((item, index) =>
      toGatewayPrincipalClientConfigSnippetView(item, index),
    ),
    claudeCodeSnippets: response.claude_code_snippets.map((item, index) =>
      toGatewayPrincipalClientConfigSnippetView(item, index),
    ),
  };
}

function toGatewayPrincipalUsageSummaryView(
  response: GetGatewayPrincipalUsageSummaryReply,
): GatewayPrincipalUsageSummaryView {
  return {
    uid: response.uid,
    label: response.label,
    days: response.days,
    totalCostUsd: response.total_cost_usd || 0,
    totalTokens: response.total_tokens || 0,
    requestCount: response.request_count || 0,
    inputTokens: response.input_tokens || 0,
    outputTokens: response.output_tokens || 0,
    cacheReadTokens: response.cache_read_tokens || 0,
    cacheWriteTokens: response.cache_write_tokens || 0,
    lastRecordedAt: response.last_recorded_at || '',
    daily: (response.daily || []).map(
      (item: GatewayPrincipalUsageDailyPoint) => ({
        usageDate: item.usage_date,
        costUsd: item.cost_usd || 0,
        totalTokens: item.total_tokens || 0,
        requestCount: item.request_count || 0,
        inputTokens: item.input_tokens || 0,
        outputTokens: item.output_tokens || 0,
        cacheReadTokens: item.cache_read_tokens || 0,
        cacheWriteTokens: item.cache_write_tokens || 0,
      }),
    ),
    models: (response.models || []).map(
      (item: GatewayPrincipalUsageModelSummary, index) => ({
        id: `${item.public_model || item.upstream_model || 'unknown'}-${index}`,
        publicModel: item.public_model || '',
        upstreamModel: item.upstream_model || '',
        costUsd: item.cost_usd || 0,
        totalTokens: item.total_tokens || 0,
        requestCount: item.request_count || 0,
        inputTokens: item.input_tokens || 0,
        outputTokens: item.output_tokens || 0,
        cacheReadTokens: item.cache_read_tokens || 0,
        cacheWriteTokens: item.cache_write_tokens || 0,
      }),
    ),
  };
}

function toGatewayDocumentView(
  response: GetGatewayPrincipalsDocumentReply,
): GatewayPrincipalsDocumentView {
  return {
    documentVersion: response.document_version,
    updatedAt: response.updated_at,
    currentVersion: response.current_version
      ? toVersionViewItem(response.current_version, 0)
      : null,
    recentHostedVersions: response.recent_hosted_versions.map((item, index) =>
      toVersionViewItem(item, index),
    ),
    recentDeployments: response.recent_deployments.map((item, index) =>
      toVersionViewItem(item, index),
    ),
    principals: response.principals.map(toPrincipalViewItem),
    templateDefaults: toTemplateDefaults(response.template_defaults),
  };
}

function toDesktopDocumentView(
  response: GetDesktopUpdatesDocumentReply,
): DesktopUpdatesDocumentView {
  const latestVersions: Record<string, Record<string, string>> = {};
  for (const item of response.latestVersions || []) {
    latestVersions[item.product] ||= {};
    latestVersions[item.product][item.channel] = item.version;
  }
  const userIdentities = Object.fromEntries(
    (response.userIdentities || []).map((identity) => [
      identity.userUid,
      identity,
    ]),
  );
  return {
    currentVersion: response.currentVersion
      ? toVersionViewItem(response.currentVersion, 0)
      : null,
    recentHostedVersions: response.recentHostedVersions.map((item, index) =>
      toVersionViewItem(item, index),
    ),
    recentDeployments: response.recentDeployments.map((item, index) =>
      toVersionViewItem(item, index),
    ),
    document: response.document,
    approvals: response.approvals || [],
    currentUserCanApprove: response.currentUserCanApprove,
    approverUids: response.approverUids || [],
    userIdentities,
    latestVersions,
    nextApprovalPageToken: response.nextApprovalPageToken || '',
  };
}

class AppConfigAdminStore {
  gatewayLoading = false;
  gatewaySaving = false;
  gatewayConfigLoading = false;
  gatewayUsageLoading = false;
  desktopLoading = false;
  desktopSaving = false;
  gatewayDocument: GatewayPrincipalsDocumentView | null = null;
  desktopDocument: DesktopUpdatesDocumentView | null = null;
  gatewayError = '';
  desktopError = '';

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  getDesktopDocument() {
    return this.desktopDocument;
  }

  isDesktopLoading() {
    return this.desktopLoading;
  }

  isDesktopSaving() {
    return this.desktopSaving;
  }

  getDesktopError() {
    return this.desktopError;
  }

  async fetchGatewayPrincipalsDocument() {
    this.gatewayLoading = true;
    this.gatewayError = '';
    try {
      const response = await appConfigAdminApi.getGatewayPrincipalsDocument();
      runInAction(() => {
        this.gatewayDocument = toGatewayDocumentView(response);
        this.gatewayLoading = false;
      });
    } catch (error) {
      const message = getErrorMessage(error, '加载内部模型授权失败');
      runInAction(() => {
        this.gatewayLoading = false;
        this.gatewayError = message;
      });
      throw new Error(message);
    }
  }

  async refreshGatewayPrincipalsDocumentAfterPublish(
    expectation: GatewayDocumentRefreshExpectation,
  ) {
    await this.fetchGatewayPrincipalsDocument();
    if (gatewayDocumentMatchesExpectation(this.gatewayDocument, expectation)) {
      return;
    }

    for (const delayMS of GATEWAY_DOCUMENT_REFRESH_DELAYS_MS) {
      await delay(delayMS);
      await this.fetchGatewayPrincipalsDocument();
      if (
        gatewayDocumentMatchesExpectation(this.gatewayDocument, expectation)
      ) {
        return;
      }
    }
  }

  async createGatewayPrincipal(data: CreateGatewayPrincipalRequest) {
    this.gatewaySaving = true;
    try {
      const response = await appConfigAdminApi.createGatewayPrincipal(data);
      await this.refreshGatewayPrincipalsDocumentAfterPublish({
        expectedUID: data.uid,
        minVersionNumber: response.published_version?.version_number,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, '创建内部模型授权失败'));
    } finally {
      runInAction(() => {
        this.gatewaySaving = false;
      });
    }
  }

  async updateGatewayPrincipal(data: UpdateGatewayPrincipalRequest) {
    this.gatewaySaving = true;
    try {
      const response = await appConfigAdminApi.updateGatewayPrincipal(data);
      await this.refreshGatewayPrincipalsDocumentAfterPublish({
        expectedUID: data.uid,
        minVersionNumber: response.published_version?.version_number,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, '更新内部模型授权失败'));
    } finally {
      runInAction(() => {
        this.gatewaySaving = false;
      });
    }
  }

  async deleteGatewayPrincipal(uid: string) {
    this.gatewaySaving = true;
    try {
      const response = await appConfigAdminApi.deleteGatewayPrincipal({ uid });
      await this.refreshGatewayPrincipalsDocumentAfterPublish({
        removedUID: uid,
        minVersionNumber: response.published_version?.version_number,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, '删除内部模型授权失败'));
    } finally {
      runInAction(() => {
        this.gatewaySaving = false;
      });
    }
  }

  async fetchDesktopUpdatesDocument(
    options: {
      appendApprovals?: boolean;
      preserveApprovalHistory?: boolean;
    } = {},
  ) {
    this.desktopLoading = true;
    this.desktopError = '';
    try {
      const currentDocument = this.desktopDocument;
      const approvalPageToken = options.appendApprovals
        ? currentDocument?.nextApprovalPageToken || ''
        : '';
      const response = await appConfigAdminApi.getDesktopUpdatesDocument(
        approvalPageToken,
      );
      runInAction(() => {
        const next = toDesktopDocumentView(response);
        if (options.appendApprovals && currentDocument) {
          const approvals = new Map(
            currentDocument.approvals.map((approval) => [
              approval.id,
              approval,
            ]),
          );
          for (const approval of next.approvals) {
            approvals.set(approval.id, approval);
          }
          next.approvals = [...approvals.values()].sort((left, right) =>
            compareDecimalIdsDescending(left.id, right.id),
          );
        } else if (options.preserveApprovalHistory && currentDocument) {
          const approvals = new Map(
            next.approvals.map((approval) => [approval.id, approval]),
          );
          for (const approval of currentDocument.approvals) {
            if (
              !approvals.has(approval.id) &&
              !isActiveDesktopApproval(approval)
            ) {
              approvals.set(approval.id, approval);
            }
          }
          next.approvals = [...approvals.values()].sort((left, right) =>
            compareDecimalIdsDescending(left.id, right.id),
          );
          next.nextApprovalPageToken = currentDocument.nextApprovalPageToken;
        }
        this.desktopDocument = next;
        this.desktopLoading = false;
      });
    } catch (error) {
      const message = getErrorMessage(error, '加载 Desktop Updates 失败');
      runInAction(() => {
        this.desktopLoading = false;
        this.desktopError = message;
      });
      throw new Error(message);
    }
  }

  async fetchMoreDesktopUpdateApprovals() {
    if (
      !this.desktopDocument?.nextApprovalPageToken ||
      this.desktopLoading
    ) {
      return;
    }
    await this.fetchDesktopUpdatesDocument({ appendApprovals: true });
  }

  async changeDesktopUpdateProduct(
    data: ChangeDesktopUpdateProductRequest,
  ) {
    this.desktopSaving = true;
    try {
      const response = await appConfigAdminApi.changeDesktopUpdateProduct(data);
      await this.fetchDesktopUpdatesDocument();
      return response;
    } catch (error) {
      if (isConflictError(error)) {
        await this.fetchDesktopUpdatesDocument().catch(() => undefined);
      }
      runInAction(() => {
        this.desktopSaving = false;
      });
      throw new Error(getErrorMessage(error, '更新 Desktop Updates 失败'));
    } finally {
      runInAction(() => {
        this.desktopSaving = false;
      });
    }
  }

  async approveDesktopUpdate(
    approvalId: string,
  ) {
    try {
      const response = await appConfigAdminApi.approveDesktopUpdate(approvalId);
      await this.fetchDesktopUpdatesDocument();
      return response.approval;
    } catch (error) {
      await this.fetchDesktopUpdatesDocument().catch(() => undefined);
      throw new Error(getErrorMessage(error, '审批 Desktop Updates 失败'));
    }
  }

  async rejectDesktopUpdate(
    approvalId: string,
    reason: string,
  ) {
    await appConfigAdminApi.rejectDesktopUpdate(approvalId, reason);
    await this.fetchDesktopUpdatesDocument();
  }

  async withdrawDesktopUpdate(approvalId: string) {
    await appConfigAdminApi.withdrawDesktopUpdate(approvalId);
    await this.fetchDesktopUpdatesDocument();
  }

  async fetchGatewayPrincipalClientConfigs(uid: string, model?: string) {
    this.gatewayConfigLoading = true;
    try {
      const response = await appConfigAdminApi.getGatewayPrincipalClientConfigs(
        uid,
        model,
      );
      return toGatewayPrincipalClientConfigsView(response);
    } catch (error) {
      throw new Error(getErrorMessage(error, '加载客户端配置失败'));
    } finally {
      runInAction(() => {
        this.gatewayConfigLoading = false;
      });
    }
  }

  async fetchGatewayPrincipalUsageSummary(uid: string, days = 30) {
    this.gatewayUsageLoading = true;
    try {
      const response = await appConfigAdminApi.getGatewayPrincipalUsageSummary(
        uid,
        days,
      );
      return toGatewayPrincipalUsageSummaryView(response);
    } catch (error) {
      throw new Error(getErrorMessage(error, '加载授权花费失败'));
    } finally {
      runInAction(() => {
        this.gatewayUsageLoading = false;
      });
    }
  }
}

export const appConfigAdminStore = new AppConfigAdminStore();
